// Admin: Services management — list / create / edit. (Catalogue re-sync now
// lives on the per-partner row in PartnersTab.)
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage, HiddenBadge } from './_shared';
import ServiceForm from './ServiceForm';

export default function AdminServicesTab() {
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axiosInstance.get('/admin/services'), // includes hidden services
        axiosInstance.get('/admin/partners'),
      ]);
      setServices(s.data);
      setPartners(p.data);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createService = async (form) => {
    try {
      await axiosInstance.post('/admin/services', form);
      toast.success('Service created');
      setView('list');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const updateService = async (form) => {
    try {
      await axiosInstance.put(`/admin/services/${editing.id}`, form);
      toast.success('Service updated');
      setView('list');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const deleteService = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axiosInstance.delete(`/admin/services/${id}`);
      toast.success('Service deleted');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  if (view === 'create')
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Add New Service</h2>
        <ServiceForm partners={partners} onSave={createService} onCancel={() => setView('list')} />
      </div>
    );

  if (view === 'edit')
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Edit Service</h2>
        <ServiceForm
          partners={partners}
          initial={editing}
          onSave={updateService}
          onCancel={() => setView('list')}
        />
      </div>
    );

  return (
    <div className="space-y-3">
      <button
        onClick={() => setView('create')}
        data-testid="admin-add-service-btn"
        className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl flex items-center justify-center gap-2"
      >
        <i className="ph-bold ph-plus" /> Add Service
      </button>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : services.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm">
          No services yet. Add manually, or visit the Partners tab to re-sync a partner catalogue.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-services-list">
          {services.map((s) => (
            <div
              key={s.id}
              className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 ${
                s.is_active === false ? 'opacity-60' : ''
              }`}
              data-testid={`admin-service-row-${s.id}`}
            >
              {s.images?.[0] && (
                <img
                  src={s.images[0]}
                  alt=""
                  className="w-14 h-14 rounded-xl object-cover shrink-0"
                  referrerPolicy="no-referrer"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-extrabold truncate">{s.name}</p>
                  {s.is_active === false && <HiddenBadge />}
                </div>
                <p className="text-xs text-gray-400">
                  {s.partner_brand || '—'} · {s.service_category || '—'}
                </p>
                <p className="text-sm font-bold text-[#E54562] mt-0.5">
                  {s.combo_only
                    ? 'On request'
                    : `from ₹${s.base_price || 0} · ${s.variants?.length || 0} variant(s)`}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => {
                    setEditing(s);
                    setView('edit');
                  }}
                  className="px-3 py-2 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs rounded-xl"
                  data-testid={`edit-service-${s.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteService(s.id, s.name)}
                  className="px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl"
                  data-testid={`delete-service-${s.id}`}
                >
                  <i className="ph-bold ph-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
